import { DT } from "../../constants.ts";
import { add, dist, flatBasis, norm, scale, sub } from "../../math.ts";
import { Btn } from "../../net/input.ts";
import { ActionKind, MoveKind, hv, setHv } from "../../sim/components.ts";
import { addOverhealth, aim, applyDamage, applyHeal, applyImpulse, applyStatus, hitscan, isEnemy, lockOn, playersInRadius } from "../../sim/combat.ts";
import { beginTargeting, endTargeting, targetConfirmed, targetPoint } from "../../sim/targeting.ts";
import { HeroId, Role, Slot, type HeroDef } from "../types.ts";
import { fxk, type HeroModule } from "../module.ts";
import { action, climb, commonInput, consume, ready, secs, startMove, swing, trigger, useUlt } from "../kitlib.ts";

export const V = { corrosionNet: 0, corrosionTicks: 1, corrosionFull: 2, burrowed: 3 } as const;
export const FX = { Tendril: fxk(HeroId.Venison, 0), Dive: fxk(HeroId.Venison, 1), Maw: fxk(HeroId.Venison, 2) } as const;
const DIVE = MoveKind.Hero;

const def: HeroDef = {
  id: HeroId.Venison,
  slug: "venison",
  name: "Venison",
  role: Role.Tank,
  color: "#5a5a82",
  blurb: "Massive dive vanguard who swings in, latches prey, converts injury into bulk, and erupts from below.",
  health: 500,
  armor: 0,
  shields: 250,
  radius: 0.62,
  height: 2.35,
  headRadius: 0.32,
  speed: 6,
  jumpSpeed: 7.45,
  ammo: 0,
  reloadTime: 0,
  ultCost: 2500,
  abilities: {
    [Slot.Primary]: { name: "Rack Predation", key: "LMB", description: "Release four piercing tendrils in a short burst.", cooldown: 0 },
    [Slot.Secondary]: { name: "Velvet Corrosion", key: "RMB", description: "Latch an enemy within 8m, slowing by 25% before a delayed burst.", cooldown: 8 },
    [Slot.Ability1]: { name: "Antler Swing", key: "Shift (hold)", description: "Swing from terrain on a dark tendril.", cooldown: 10 },
    [Slot.Ability2]: { name: "Rutting Resilience", key: "E", description: "Gain bonus health based on missing health.", cooldown: 15 },
    [Slot.Ability3]: { name: "Frenzied Landing", key: "F", description: "Aim a landing point up to 40m, then press LMB to dive, damage, and knock enemies up.", cooldown: 8 },
    [Slot.Ultimate]: { name: "Feast Below", key: "Q", description: "Burrow with a 7m devour ring for up to 4 seconds; press LMB to erupt early and convert damage to bonus health.", cooldown: 0 },
  },
  passive: { name: "Alien Antlers", description: "Hold Space into a wall to crawl; hold Primary while crawling to sprint upward." },
};

function finishCorrosion(sim: Parameters<HeroModule["kit"]["tick"]>[0], eid: number) {
  const target = sim.eidOf(Math.round(hv(sim.c, eid, V.corrosionNet)));
  const full = hv(sim.c, eid, V.corrosionFull) > 0;
  if (target !== undefined && sim.c.Player.alive[target] && isEnemy(sim, eid, target)) {
    const dealt = applyDamage(sim, target, full ? 80 : 30, eid);
    applyHeal(sim, eid, eid, dealt * 0.5, { noUlt: true });
  }
  setHv(sim.c, eid, V.corrosionNet, 0);
  setHv(sim.c, eid, V.corrosionTicks, 0);
  setHv(sim.c, eid, V.corrosionFull, 0);
}

function impactDive(sim: Parameters<HeroModule["kit"]["tick"]>[0], eid: number) {
  const p = add(sim.posOf(eid), { x: 0, y: 0.7, z: 0 });
  sim.emit({ type: "explosion", pos: p, radius: 6, kind: FX.Dive });
  for (const { eid: t, d } of playersInRadius(sim, p, 6, (e) => isEnemy(sim, eid, e), false)) {
    const falloff = d <= 2 ? 1 : Math.max(0.4, 1 - ((d - 2) / 4) * 0.6);
    applyDamage(sim, t, 65 * falloff, eid);
    applyStatus(sim, t, "slow", 1, { slowPct: 0.35, source: eid });
    sim.c.Vel.y[t] = Math.max(sim.c.Vel.y[t]!, 8);
    sim.c.Player.grounded[t] = 0;
  }
}

function erupt(sim: Parameters<HeroModule["kit"]["tick"]>[0], eid: number) {
  const p = add(targetPoint(sim, eid, Slot.Ultimate).pos, { x: 0, y: 0.7, z: 0 });
  sim.emit({ type: "explosion", pos: p, radius: 7, kind: FX.Maw });
  let total = 0;
  for (const { eid: t } of playersInRadius(sim, p, 7, (e) => isEnemy(sim, eid, e), false)) {
    const hp = sim.c.Health.hp[t]! + sim.c.Health.armor[t]! + sim.c.Health.shield[t]!;
    total += applyDamage(sim, t, 50 + hp * 0.5, eid);
    applyStatus(sim, t, "antiHeal", 4, { antiHealPct: 0.3, source: eid });
  }
  if (total > 0) addOverhealth(sim, eid, total * 1.3, secs(6), eid);
  sim.c.Ult.active[eid] = 0;
  setHv(sim.c, eid, V.burrowed, 0);
  endTargeting(sim, eid);
  action(sim, eid, ActionKind.Ult, 20);
}

const module: HeroModule = {
  def,
  kit: {
    init() {},
    tick(sim, eid, input, pressed, canAct) {
      const c = sim.c;
      const corTicks = hv(c, eid, V.corrosionTicks);
      if (corTicks > 0) {
        const t = sim.eidOf(Math.round(hv(c, eid, V.corrosionNet)));
        setHv(c, eid, V.corrosionTicks, corTicks - 1);
        if (t !== undefined && c.Player.alive[t]) {
          applyStatus(sim, t, "slow", 0.2, { slowPct: 0.25, source: eid });
          const d = dist(sim.posOf(eid), sim.posOf(t));
          if (d <= 11) {
            setHv(c, eid, V.corrosionFull, 1);
            applyImpulse(sim, t, scale(sub(sim.posOf(eid), sim.posOf(t)), 0.25));
            applyHeal(sim, eid, eid, 10 * DT, { noUlt: true, silent: true });
          } else finishCorrosion(sim, eid);
        }
        if (hv(c, eid, V.corrosionTicks) <= 0) finishCorrosion(sim, eid);
      }

      if (c.Ult.active[eid]! > 0) {
        if (!canAct) {
          c.Ult.active[eid] = 0;
          setHv(c, eid, V.burrowed, 0);
          endTargeting(sim, eid);
          return;
        }
        setHv(c, eid, V.burrowed, 1);
        applyStatus(sim, eid, "phased", 0.2, { source: eid });
        applyStatus(sim, eid, "haste", 0.2, { hastePct: 0.8, source: eid });
        if (targetConfirmed(sim, eid, Slot.Ultimate) || c.Ult.active[eid]! <= 1) erupt(sim, eid);
        return;
      }

      commonInput(sim, eid, input, pressed);
      if (!canAct) return;

      if (useUlt(sim, eid, input, pressed)) {
        c.Ult.active[eid] = secs(4) + 1;
        setHv(c, eid, V.burrowed, 1);
        beginTargeting(sim, eid, Slot.Ultimate);
      }

      if ((pressed & Btn.Ability1) && ready(sim, eid, Slot.Ability1) && swing(sim, eid, input, Btn.Ability1, { range: 30, seconds: 2.2 })) consume(sim, eid, Slot.Ability1);

      if ((pressed & Btn.Ability2) && ready(sim, eid, Slot.Ability2)) {
        consume(sim, eid, Slot.Ability2);
        const lost = c.Health.maxHp[eid]! + c.Health.maxArmor[eid]! + c.Health.maxShield[eid]! - (c.Health.hp[eid]! + c.Health.armor[eid]! + c.Health.shield[eid]!);
        addOverhealth(sim, eid, 100 + lost * 1.1, secs(5), eid);
        action(sim, eid, ActionKind.Cast, 12);
      }

      if ((pressed & Btn.Ability3) && ready(sim, eid, Slot.Ability3)) {
        consume(sim, eid, Slot.Ability3);
        const t = targetPoint(sim, eid, Slot.Ability3);
        startMove(sim, eid, DIVE, secs(1.25), 32, t.pos);
        action(sim, eid, ActionKind.Cast, 16);
      }

      if ((pressed & Btn.Secondary) && ready(sim, eid, Slot.Secondary)) {
        const t = lockOn(sim, eid, 8, 0.75, false);
        if (t !== undefined) {
          consume(sim, eid, Slot.Secondary);
          setHv(c, eid, V.corrosionNet, c.Net.id[t]!);
          setHv(c, eid, V.corrosionTicks, secs(3));
          setHv(c, eid, V.corrosionFull, 1);
          applyDamage(sim, t, 5, eid);
          applyStatus(sim, t, "slow", 3, { slowPct: 0.25, source: eid });
          action(sim, eid, ActionKind.AltFire, 12);
        }
      }

      if (trigger(sim, eid, !!(input.buttons & Btn.Primary), true)) {
        c.Weapon.fireCd[eid] = secs(0.9);
        action(sim, eid, ActionKind.Fire, 10);
        const a = aim(sim, eid);
        for (let i = 0; i < 4; i++) {
          const yaw = c.Look.yaw[eid]! + (i - 1.5) * 0.025;
          const dir = norm(add(a.dir, { x: Math.sin(yaw) * 0.02, y: 0, z: Math.cos(yaw) * 0.02 }));
          hitscan(sim, eid, a.origin, dir, { range: 15, damage: 20, canCrit: true, fxKind: FX.Tendril, tracer: i === 0 });
        }
      }
    },
    movement(sim, eid, input, mods) {
      if (sim.c.Ult.active[eid]! > 0) {
        mods.speedMul *= 1;
        mods.gravityMul = 0.2;
      }
      const swinging = swing(sim, eid, input, Btn.Ability1, { range: 30, seconds: 2.2 });
      const climbing = !swinging && !!(input.buttons & Btn.Jump) && climb(sim, eid, input, mods, { speed: input.buttons & Btn.Primary ? 9 : 3 });
      void flatBasis;
      setHv(sim.c, eid, V.burrowed, sim.c.Ult.active[eid]! > 0 ? 1 : 0);
      if (climbing) setHv(sim.c, eid, V.burrowed, 2);
    },
    scripted(sim, eid, _input, kind) {
      if (kind !== DIVE) return;
      const target = { x: sim.c.Move.x[eid]!, y: sim.c.Move.y[eid]!, z: sim.c.Move.z[eid]! };
      const p = sim.posOf(eid);
      const d = dist(p, target);
      if (d < 1.2 || sim.c.Move.blocked[eid]) {
        impactDive(sim, eid);
        sim.c.Move.ticks[eid] = 0;
        return;
      }
      const dir = norm(sub(target, p));
      sim.c.Vel.x[eid] = dir.x * 32;
      sim.c.Vel.y[eid] = dir.y * 32;
      sim.c.Vel.z[eid] = dir.z * 32;
    },
  },
  fx: {
    [FX.Tendril]: { color: "#2c2d3f" },
    [FX.Dive]: { color: "#d2b48c" },
    [FX.Maw]: { color: "#5a5a82" },
  },
  targets: {
    [Slot.Ability3]: { range: 40, radius: 6, verb: "Dive" },
    [Slot.Ultimate]: { range: 0, radius: 7, start: "kit", self: true, timeout: 4, verb: "Devour" },
  },
};

export default module;
